import { Autocomplete, TextField } from "@mui/material";
import * as React from "react";

import { isNumericField } from "@/shared/domain/urdf";

import styles from "./TopicPathAutocomplete.module.css";
import { useTopicPathParts } from "./useTopicPathParts";

/**
 * Suggestions offered at once. The org catalogue is capped at 10k topic names
 * and every option becomes a DOM node, so the menu takes the first page of
 * matches rather than the whole match list.
 */
const MAX_SUGGESTIONS = 50;

interface TopicPathAutocompleteProps {
  /** The joined `topic.message.path` text. Controlled by the caller. */
  value: string;
  onChange: (next: string) => void;
  /** Whose catalogue to search. */
  orgId?: string;
  label?: string;
  /** Accessible name when the field has no visible label (e.g. a dense row). */
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * A two-stage topic-and-field picker over the org's catalogue.
 *
 * Stage one offers topic names; typing a `.` after a known one switches the menu
 * to that topic's own field paths. The stages exist because the two catalogues
 * are different sizes and different shapes — 10k topic names org-wide, a few
 * dozen fields per topic — and because the boundary between them cannot be found
 * by looking for a dot. `useTopicPathParts` owns that split; this component only
 * renders it.
 *
 * ## Free text is accepted, on purpose
 *
 * `freeSolo`, and nothing here requires the typed topic to exist. The catalogue
 * is eventually consistent and capped at 10k, and the normal state when
 * ingesting a robot description is that **no recording exists yet at all** — the
 * URDF is being ingested before the robot has been driven. A picker that only
 * accepted catalogued names would refuse the commonest case there is.
 *
 * Modelled on `Agents/Skills/TopicBodyEditor`'s two-stage autocomplete, which
 * solved the same problem inside a textarea. This is the single-line form, so
 * the caret-token machinery is not needed and MUI's own listbox, keyboard
 * navigation and `autoHighlight` do the rest — including re-highlighting when
 * the field list arrives asynchronously and the option list changes under it.
 */
export function TopicPathAutocomplete({
  value,
  onChange,
  orgId,
  label,
  ariaLabel,
  placeholder,
}: TopicPathAutocompleteProps) {
  const parts = useTopicPathParts(value, orgId);

  const options = React.useMemo(() => {
    if (!parts.fieldMode) {
      const query = parts.topic.toLowerCase();
      return parts.topicNames
        .filter((name) => name.toLowerCase().includes(query))
        .slice(0, MAX_SUGGESTIONS);
    }
    // Matched against the field alone, so a path that already carries an index
    // (`position[3]`) still matches `position` rather than emptying the menu.
    const query = parts.messageField.toLowerCase();
    return parts.fields
      .filter(isNumericField)
      .filter((field) => field.path.toLowerCase().includes(query))
      .map((field) => `${parts.topic}.${field.path}`)
      .slice(0, MAX_SUGGESTIONS);
  }, [
    parts.fieldMode,
    parts.topic,
    parts.messageField,
    parts.topicNames,
    parts.fields,
  ]);

  return (
    <Autocomplete
      autoHighlight
      // Filtering is done above, against the catalogue and the two-stage split.
      // MUI's own filter would layer a second, different rule on top of it.
      filterOptions={(shown) => shown}
      freeSolo
      inputValue={value}
      loading={parts.isLoading}
      onInputChange={(_event, next) => onChange(next)}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            ...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {}),
          }}
          label={label}
          placeholder={placeholder}
          size="small"
        />
      )}
      size="small"
      // The menu sizes to the path it is completing rather than to the input.
      // Applied to the paper on purpose — see `TopicPathAutocomplete.module.css`
      // for why the popper keeps MUI's anchor-width style.
      slotProps={{ paper: { className: styles.paper } }}
    />
  );
}
